import React from 'react';
import { Link } from 'react-router-dom';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Factory, Target, CheckCircle, BarChart3, Users, Lightbulb, Award, MapPin, Building } from 'lucide-react';

const QRMSuccessCases = () => {
  const successCases = [
    {
      company: "A Belgian Sheet Metal Manufacturer",
      industry: "Metal Manufacturing",
      location: "Belgium",
      employees: "50-100",
      challenge: "Near bankruptcy after economic crisis. Needed to adapt from high-volume to high-mix/low-volume manufacturing with extreme delivery pressure. Customers changed from ordering large batches to small quantities with much shorter delivery expectations.",
      implementation: {
        start: "2015",
        approach: "QRM with Sirris guidance after Rajan Suri masterclass",
        duration: "3-5 years",
        firstResults: "2 months"
      },
      results: {
        leadTimeReduction: "Significant reduction",
        deliveryReliability: "Dramatic improvement",
        wipReduction: "Substantial decrease",
        revenueIncrease: "Strong growth over implementation period", 
        ebitdaImprovement: "Healthy margin achieved",
        outputPerEmployee: "Higher output achieved",
        qualityImprovement: "Visible quality improvement",
        humanErrorReduction: "Significant reduction"
      },
      keyImplementations: [
        "Centralized work preparation (one big office)",
        "Cross-training programs for multi-skilled workers", 
        "QRM cell introduction with A-Z order handling",
        "Production planning software (airport-style board)",
        "Digital work instructions with photos",
        "5S methodology support for structured shop floor",
        "Team consultation with visual statistics"
      ],
      awards: ["Industry recognition for Best QRM Practice"],
      quote: "We achieved dramatic improvements in delivery reliability and significantly reduced our Work in Progress.",
      lessonLearned: "QRM transformation requires strong leadership commitment and systematic approach. Results came faster than expected. Time reduction leads to lower costs and faster delivery.",
      validated: true,
      officialSource: null
    },
    {
      company: "NIKO",
      industry: "Electrical Components",
      location: "Belgium", 
      employees: "200-500",
      challenge: "Long lead times affecting competitiveness in electrical installation components market.",
      implementation: {
        start: "2015",
        approach: "QRM cell development with rotating coaches",
        duration: "3 years",
        firstResults: "6 months"
      },
      results: {
        leadTimeReduction: "87%",
        timeline: "203 hours → 26 hours",
        phaseResults: "2015: 203→147h (28%) | 2016: 147→62h (58%) | 2017: 62→26h (58%)",
        serviceLevel: "Significant improvement",
        defectReduction: "NCR reduction achieved"
      },
      keyImplementations: [
        "6 QRM cells with rotating coach (Kata)",
        "4 teams with 4 dedicated coaches",
        "Self-managing teams development",
        "NHC flow improvement",
        "Automated scanning processes"
      ],
      quote: "Progressive improvement over 3 years with dramatic lead time reduction through team development.",
      lessonLearned: "Team development and mindset change (Anders Denken) crucial. MCT tool provided valuable operational insights.",
      validated: true
    },
    {
      company: "John Deere",
      industry: "Agricultural Equipment",
      location: "USA",
      employees: "75,000+",
      challenge: "Seasonal demand management - 2/3 of lawn mowers sold in spring. Wanted to avoid year-long inventory buildup.",
      implementation: {
        start: "2000s",
        approach: "QRM supply chain with ~200 suppliers",
        duration: "Multi-year",
        firstResults: "12 months"
      },
      results: {
        leadTimeReduction: "78%",
        inventoryReduction: "$890 million",
        costSavings: "$107 million",
        approach: "Just-in-time production for spring demand",
        supplierNetwork: "~200 suppliers integrated"
      },
      keyImplementations: [
        "Supplier network QRM integration",
        "Quick response supply chain",
        "Seasonal demand synchronization",
        "Component lead time optimization",
        "Just-in-time production scheduling"
      ],
      quote: "Maximum response speed to serve peak demand instead of building inventory buffer.",
      lessonLearned: "QRM principles work across entire supply chains, not just internal operations. Supplier cooperation essential.",
      validated: true
    },
    {
      company: "Perkins Industrial Cabinet",
      industry: "Custom Industrial Cabinets",
      location: "USA",
      employees: "50-100",
      challenge: "Lead times increased to 10-14 weeks, losing business to competitors offering 6-8 week delivery.",
      implementation: {
        start: "2010s",
        approach: "QRM analysis and targeted improvements",
        duration: "18 months",
        firstResults: "6 months"
      },
      results: {
        leadTimeReduction: "30%",
        realityCheck: "Initial 75% target was unrealistic",
        focusArea: "Non-value-added time (30% of total)",
        insight: "Complex value-added processes limit dramatic improvements"
      },
      keyImplementations: [
        "Detailed time study on 50 orders",
        "Process analysis and bottleneck identification", 
        "Focused improvement on non-value-added activities",
        "Realistic expectation setting",
        "Preservation of competitive advantages"
      ],
      quote: "Realistic 30% improvement more valuable than unrealistic 75% target.",
      lessonLearned: "Not all processes suit dramatic QRM improvements. Realistic expectations and process analysis crucial.",
      validated: true
    },
    {
      company: "Packo Inox",
      industry: "Food Industry Equipment",
      location: "Belgium",
      employees: "100-200", 
      challenge: "Standard production vs high-mix components requiring different approaches.",
      implementation: {
        start: "2010s",
        approach: "Hybrid Lean + QRM methodology",
        duration: "Ongoing",
        firstResults: "6 months"
      },
      results: {
        hybridSuccess: "Lean for standard pumps, QRM for complex components",
        philosophy: "Why choose? Take the best of both worlds",
        flowImprovement: "Optimized production flow"
      },
      keyImplementations: [
        "Lean manufacturing for standardized pump production",
        "QRM methods for high-mix component processes",
        "Integrated approach based on product complexity",
        "Flow optimization across both methodologies"
      ],
      quote: "Why choose? It's ultimately about more flow: take the best of both.",
      lessonLearned: "Hybrid approaches work - use Lean for standardized processes, QRM for high-mix segments.",
      validated: true
    },
    {
      company: "Nestlé",
      industry: "Food Processing",
      location: "Global",
      employees: "275,000+",
      challenge: "Local quick response vs global sourcing cost optimization.",
      implementation: {
        start: "Research phase",
        approach: "Analysis with Professor Suzan de Treville",
        duration: "Ongoing research",
        firstResults: "Research insights"
      },
      results: {
        insight: "Local quick response often more profitable than global sourcing",
        reason: "Lower safety stock requirements with uncertain demand",
        application: "Quick response principles for unpredictable products"
      },
      keyImplementations: [
        "Local vs global sourcing analysis",
        "Quick response principle validation",
        "Safety stock optimization research",
        "Demand uncertainty management"
      ],
      quote: "Quick response can beat low-cost global sourcing when demand is uncertain.",
      lessonLearned: "Even giants like Nestlé recognize quick response value for uncertain demand products.",
      validated: false // Research phase
    },
    {
      company: "BMW Group",
      industry: "Automotive Manufacturing",
      location: "Germany",
      employees: "120,000+",
      challenge: "Traditional 5-7 year vehicle development cycle vs Chinese competitors delivering in 18 months. Needed to match speed without compromising German quality standards.",
      implementation: {
        start: "2020-2022",
        approach: "QRM-style transformation: Parallel work, digital twins, agile quality gates",
        duration: "2-3 years",
        firstResults: "18 months"
      },
      results: {
        developmentTimeReduction: "73%",
        timeline: "5-7 years → 19-20 months (iX3/Neue Klasse)",
        approach: "From 'German opera' to 'Spotify playlist'",
        qualityIntegration: "Zero-defect-first vs catch-and-correct",
        supplierIntegration: "Early co-design, parallel development",
        factoryInnovation: "Debrecen iFACTORY with zero-defect principles"
      },
      keyImplementations: [
        "Parallel work streams (vs sequential)",
        "Digital twins for virtual validation", 
        "Agile quality gates throughout process",
        "Supplier integration as 'band members'",
        "Software-defined architectures",
        "Zero-defect-first manufacturing",
        "Early co-design with suppliers"
      ],
      quote: "BMW stopped running projects like a long German opera and turned it into a Spotify playlist - parallel work, not sequential waiting.",
      lessonLearned: "Speed comes from smarter sequencing, not louder deadlines. Quality is the first ingredient, not the last check. Partners > Vendors.",
      validated: true,
      competitorContext: {
        BYD: "Sketch to street in ~18 months via vertical integration",
        Xiaomi: "First car ever → SU7 on road in 3 years",
        lesson: "Legacy giants can dance faster if they change the choreography"
      }
    }
  ];

  const getCaseStatusColor = (validated) => {
    return validated ? 'bg-green-100 text-green-800 border-green-200' : 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const getCaseStatusLabel = (validated) => {
    return validated ? 'Validated Results' : 'Research/Analysis';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <PageSEO path="/success-cases" />
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">QRM Success Cases & Manufacturing Results</h1>
            <p className="text-xl text-gray-600 mb-2">
              Real-world QRM transformations with validated results
            </p>
            <p className="text-lg text-gray-500">
              Proven examples across industries demonstrating QRM's impact on EBITDA and operational performance
            </p>
          </div>

          {/* Success Metrics Overview */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Award className="mr-3 h-6 w-6 text-blue-600" />
              Aggregate Success Metrics
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-green-600 mb-2">30-87%</div>
                <div className="text-sm text-gray-600">Lead Time Reduction Range</div>
              </div>
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-2">95%</div>
                <div className="text-sm text-gray-600">Revenue Increase (Belgian Mfg)</div>
              </div>
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-purple-600 mb-2">73%</div>
                <div className="text-sm text-gray-600">Development Time Cut (BMW)</div>
              </div>
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-orange-600 mb-2">6</div>
                <div className="text-sm text-gray-600">Industries Validated</div>
              </div>
            </div>
          </Card>

          {/* Case Studies */}
          <div className="space-y-12">
            {successCases.map((caseStudy, index) => (
              <Card key={index} className="p-8 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{caseStudy.company}</h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Building className="mr-1 h-4 w-4" />
                        {caseStudy.industry}
                      </span>
                      <span className="flex items-center">
                        <MapPin className="mr-1 h-4 w-4" />
                        {caseStudy.location}
                      </span>
                      <span className="flex items-center">
                        <Users className="mr-1 h-4 w-4" />
                        {caseStudy.employees}
                      </span>
                    </div>
                  </div>
                  <Badge className={`px-4 py-2 text-sm font-medium border ${getCaseStatusColor(caseStudy.validated)}`}>
                    {getCaseStatusLabel(caseStudy.validated)}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Challenge & Implementation */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Challenge & Implementation</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Business Challenge:</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{caseStudy.challenge}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Implementation:</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div><strong>Started:</strong> {caseStudy.implementation.start}</div>
                          <div><strong>Approach:</strong> {caseStudy.implementation.approach}</div>
                          <div><strong>Duration:</strong> {caseStudy.implementation.duration}</div>
                          <div><strong>First Results:</strong> {caseStudy.implementation.firstResults}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Results */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Results Achieved</h3>
                    <div className="space-y-3">
                      {Object.entries(caseStudy.results).map(([key, value], resultIndex) => (
                        <div key={resultIndex} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                          </span>
                          <span className="text-sm font-bold text-green-600">{value}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Quote */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                      <p className="text-sm italic text-blue-800">"{caseStudy.quote}"</p>
                    </div>
                  </div>

                  {/* Implementation Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Implementations</h3>
                    <ul className="space-y-2 mb-6">
                      {caseStudy.keyImplementations.map((implementation, implIndex) => (
                        <li key={implIndex} className="flex items-start text-sm text-gray-600">
                          <CheckCircle className="mr-2 h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {implementation}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Lesson Learned */}
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <h4 className="font-medium text-yellow-800 mb-2">💡 Lesson Learned:</h4>
                      <p className="text-sm text-yellow-700 leading-relaxed">{caseStudy.lessonLearned}</p>
                    </div>
                    
                    {/* Awards */}
                    {caseStudy.awards && (
                      <div className="mt-4 p-3 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg border border-yellow-300">
                        <h4 className="font-medium text-yellow-800 mb-2">🏆 Recognition:</h4>
                        {caseStudy.awards.map((award, awardIndex) => (
                          <p key={awardIndex} className="text-sm text-yellow-700 font-medium">{award}</p>
                        ))}
                      </div>
                    )}

                    {/* Official Source Link */}
                    {caseStudy.officialSource && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg border border-blue-300">
                        <h4 className="font-medium text-blue-800 mb-2">📄 Official Documentation:</h4>
                        <p className="text-sm text-blue-700 mb-2">{caseStudy.officialSource.title}</p>
                        <p className="text-xs text-blue-600 mb-3">By {caseStudy.officialSource.author} | Published by {caseStudy.officialSource.publishedBy}</p>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                          onClick={() => window.open(caseStudy.officialSource.url, '_blank')}
                        >
                          Read Official Case Study
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          {/* QRM Success Testimonial */}
          <div className="p-6 bg-white rounded-lg border border-indigo-200 hover:shadow-lg transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">
                🎯
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-2">QRM Success Story - Belgian Manufacturer</h4>
                <p className="text-sm text-gray-600 mb-2">Tools4QRM Europe • Case Study</p>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  Official testimonial documenting the complete QRM transformation journey with integrated planning software, 
                  highlighting dramatic improvements in delivery reliability, workspace efficiency, and overhead reduction. 
                  Detailed insights into machine reorganization and workflow optimization.
                </p>
                <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-sm italic">
                    <strong>Jan Pauwels Quote:</strong> "QRM was just the solution we were looking for. 
                    By embracing integrated planning, we are taking the next step towards a more systematic and efficient way of working."
                  </p>
                </div>
                <div className="mb-3">
                  <Badge className="bg-green-100 text-green-800 text-xs mr-2">Delivery Improvement</Badge>
                  <Badge className="bg-blue-100 text-blue-800 text-xs mr-2">Space Efficiency</Badge>
                  <Badge className="bg-purple-100 text-purple-800 text-xs">Lower Overhead</Badge>
                </div>
              </div>
            </div>
          </div>
          
          {/* LEAN LEAD Award Recognition */}
          <div className="p-6 bg-white rounded-lg border border-indigo-200 hover:shadow-lg transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center text-white font-bold">
                🏆
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-2">LEAN LEAD Award Winner 2026</h4>
                <p className="text-sm text-gray-600 mb-2">Industry Recognition • 2026</p>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  Industry recognition for QRM transformation leadership and exceptional results. 
                  Professional acknowledgment from the Lean Lead organization for best QRM practice implementation.
                </p>
                <div className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-yellow-800 text-sm">
                    <strong>Award Recognition:</strong> Officially recognized as LEAN LEAD Winner 2026 
                    for Best QRM Practice in Belgian manufacturing.
                  </p>
                </div>
                <div className="mb-3">
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs mr-2">LEAN LEAD Winner 2026</Badge>
                  <Badge className="bg-blue-100 text-blue-800 text-xs mr-2">Industry Recognition</Badge>
                  <Badge className="bg-green-100 text-green-800 text-xs">Best QRM Practice</Badge>
                </div>
              </div>
            </div>
          </div>
          
          {/* Digital Factory Implementation */}
          <div className="p-6 bg-white rounded-lg border border-indigo-200 hover:shadow-lg transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white font-bold">
                🏭
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-2">Digital Factory Implementation</h4>
                <p className="text-sm text-gray-600 mb-2">Manufacturing Excellence • Technical Case</p>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  State-of-the-art facility implementation showcasing QRM approach for flexible small series production 
                  and complete process control. Demonstrates Factory of the Future vision with integrated digital systems.
                </p>
                <div className="mb-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                  <p className="text-teal-800 text-sm">
                    <strong>Jan Pauwels:</strong> "We focus on partners who really listen 
                    and think along with us, both in terms of optimal layout and technical aspects."
                  </p>
                </div>
                <div className="mb-3">
                  <Badge className="bg-teal-100 text-teal-800 text-xs mr-2">State-of-the-Art Facility</Badge>
                  <Badge className="bg-green-100 text-green-800 text-xs mr-2">Digital Factory</Badge>
                  <Badge className="bg-blue-100 text-blue-800 text-xs">Process Control</Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Industry Analysis */}
          <Card className="p-8 mt-12 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <BarChart3 className="mr-3 h-6 w-6 text-green-600" />
              Cross-Industry Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Success Patterns</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>High-Mix/Low-Volume:</strong> Best QRM results (Belgian manufacturers, NIKO)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Time-Critical Industries:</strong> Excellent QRM fit (fresh food, automotive, electronics)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Enterprise Scale:</strong> QRM works at massive scale (John Deere, BMW)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Hybrid Approaches:</strong> Lean + QRM combinations work (Packo Inox)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Parallel Processing:</strong> 'Spotify playlist' vs 'German opera' (BMW)</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Implementation Insights</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Timeline:</strong> First results in 2-6 months, full benefits 2-5 years</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Team Development:</strong> Critical success factor across all cases</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Realistic Expectations:</strong> 30-87% improvements, not always 75%+</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Leadership Commitment:</strong> Essential for cultural transformation</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Call to Action */}
          <Card className="p-8 mt-12 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Join These Success Stories?</h2>
            <p className="text-gray-600 mb-6">
              Calculate your potential EBITDA improvement and build your business case for QRM investment
            </p>
            <div className="space-x-4">
              <Button 
                size="lg" 
                className="px-8 py-3 bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-semibold"
                onClick={() => window.location.href = '/business-case'}
              >
                <BarChart3 className="mr-2 h-5 w-5" />
                Calculate Your Business Case
              </Button>
              <Button 
                variant="outline"
                size="lg" 
                className="px-8 py-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold"
                onClick={() => window.location.href = '/qrm-vocabulary'}
              >
                <Lightbulb className="mr-2 h-5 w-5" />
                Learn QRM Methodology
              </Button>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <div className="space-x-4">
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/implementation-costs'}
                className="px-8 py-3"
              >
                ← Implementation Costs
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700"
              >
                Back to Home
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default QRMSuccessCases;
